import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Plus, Loader2, Copy, Globe2 } from "lucide-react";
import { API, authHeaders } from "@/lib/site";

const inputCls =
  "px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary/60";

export default function EmailTab() {
  const [domains, setDomains] = useState([]);
  const [mailboxes, setMailboxes] = useState([]);
  const [domain, setDomain] = useState("");
  const [localPart, setLocalPart] = useState("");
  const [domainId, setDomainId] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const [d, m] = await Promise.all([
        axios.get(`${API}/domains`, { headers: authHeaders() }),
        axios.get(`${API}/mailboxes`, { headers: authHeaders() }),
      ]);
      setDomains(d.data.domains);
      setMailboxes(m.data.mailboxes);
      if (d.data.domains.length && !domainId) setDomainId(d.data.domains[0].domain_id);
    } catch {
      toast.error("Could not load email data.");
    }
  }, [domainId]);

  useEffect(() => { load(); }, [load]);

  const addDomain = async (e) => {
    e.preventDefault();
    setBusy("domain");
    try {
      await axios.post(`${API}/domains`, { domain }, { headers: authHeaders() });
      toast.success("Domain added.");
      setDomain("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add domain.");
    } finally {
      setBusy("");
    }
  };

  const requestMailbox = async (e) => {
    e.preventDefault();
    if (!domainId) return toast.error("Add a domain first.");
    setBusy("mailbox");
    try {
      await axios.post(`${API}/mailboxes`, { domain_id: domainId, local_part: localPart }, { headers: authHeaders() });
      toast.success("Mailbox requested — the Wynova team will provision it and the password will appear here.");
      setLocalPart("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not request mailbox.");
    } finally {
      setBusy("");
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard.");
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div>
        <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><Globe2 className="w-5 h-5 text-primary" /> Your Domains</h3>
        <form onSubmit={addDomain} className="flex gap-3 mb-5">
          <input required placeholder="yourbusiness.com" value={domain} onChange={(e) => setDomain(e.target.value)}
                 className={`${inputCls} flex-1`} data-testid="domain-add-input" />
          <button type="submit" disabled={busy === "domain"} data-testid="domain-add-submit"
                  className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full bg-primary text-white font-semibold text-sm hover:bg-blue-700 disabled:opacity-60 transition-colors">
            {busy === "domain" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
          </button>
        </form>
        {domains.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="domains-empty">No domains yet — add the domain you want email on.</p>
        ) : (
          <div className="space-y-3" data-testid="domains-list">
            {domains.map((d) => (
              <div key={d.domain_id} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-5 py-4 flex justify-between items-center">
                <span className="font-mono text-sm">{d.domain}</span>
                <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">{d.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><Mail className="w-5 h-5 text-primary" /> Mailboxes</h3>
        <form onSubmit={requestMailbox} className="flex flex-col sm:flex-row gap-3 mb-5">
          <select value={domainId} onChange={(e) => setDomainId(e.target.value)} className={`${inputCls} sm:w-48`} data-testid="mailbox-domain-select">
            {domains.length === 0 && <option value="">Add a domain first</option>}
            {domains.map((d) => <option key={d.domain_id} value={d.domain_id}>@{d.domain}</option>)}
          </select>
          <input required placeholder="info" value={localPart} onChange={(e) => setLocalPart(e.target.value)}
                 className={`${inputCls} flex-1`} data-testid="mailbox-local-input" />
          <button type="submit" disabled={busy === "mailbox" || !domains.length} data-testid="mailbox-request-submit"
                  className="inline-flex justify-center items-center gap-1.5 px-5 py-3 rounded-full bg-primary text-white font-semibold text-sm hover:bg-blue-700 disabled:opacity-60 transition-colors">
            {busy === "mailbox" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Request
          </button>
        </form>
        {mailboxes.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="mailboxes-empty">No mailboxes yet — request one and the team will set it up.</p>
        ) : (
          <div className="space-y-3" data-testid="mailboxes-list">
            {mailboxes.map((m) => (
              <div key={m.mailbox_id} data-testid={`mailbox-row-${m.mailbox_id}`}
                   className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-5 py-4">
                <div className="flex justify-between items-center gap-3">
                  <span className="font-mono text-sm truncate">{m.address}</span>
                  <span className={`text-xs font-semibold uppercase tracking-wider shrink-0 ${
                    m.status === "provisioned" ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"
                  }`}>
                    {m.status === "provisioned" ? "Ready" : "Provisioning"}
                  </span>
                </div>
                {m.status === "provisioned" && m.password && (
                  <div className="mt-3 flex items-center justify-between rounded-xl bg-slate-950 text-slate-200 px-4 py-2.5 font-mono text-xs">
                    <span className="text-slate-500">password</span>
                    <span className="flex items-center gap-2">{m.password}
                      <button onClick={() => copy(m.password)} aria-label="Copy mailbox password" className="text-slate-500 hover:text-white">
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
